'use client';

import { motion } from 'framer-motion';
import CardFlip from '@/components/ui/CardFlip';
import { BrainCog } from 'lucide-react';
import React from 'react';

const ProcessSection = () => {
  const processPhases = [
    {
      title: 'Phase One',
      subtitle: 'Planning & Strategy',
      description:
        "I'll collaborate to map out your goals, growth anticipation, and key functionalities. We'll discuss things like department structure, communications, and infrastructure requirements.",
      features: [
        'Goal Mapping',
        'Growth Planning',
        'Infrastructure Design',
        'Communication Strategy',
      ],
      theme: 'primary' as const,
      phaseNumber: '1',
    },
    {
      title: 'Phase Two',
      subtitle: 'Development & Testing',
      description:
        'Creating structured builds of your solution with precision, implementing best practices and rigorous testing protocols while keeping security as top priority.',
      features: [
        'Structured Development',
        'Best Practices',
        'Rigorous Testing',
        'Security First',
      ],
      theme: 'emerald' as const,
      phaseNumber: '2',
    },
    {
      title: 'Phase Three',
      subtitle: 'Launch & Support',
      description:
        'Deploying your solution with minimal disruption, confidence and provide ongoing support to ensure long-term success and growth.',
      features: [
        'Smooth Deployment',
        'Minimal Disruption',
        'Ongoing Support',
        'Long-term Success',
      ],
      theme: 'orange' as const,
      phaseNumber: '3',
    },
  ];

  return (
    <section className="from-card/0 via-background/10 to-background relative overflow-hidden bg-gradient-to-br py-20 md:py-40">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="bg-accent/5 absolute top-1/2 left-3/4 h-150 w-150 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16 text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-primary/10 border-primary/20 mb-6 inline-flex items-center gap-3 rounded-full border px-6 py-3"
          >
            <BrainCog className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-400">
              Development
            </span>
          </motion.div>
          <div className="from-foreground via-primary to-foreground mb-2 bg-gradient-to-r bg-clip-text text-4xl leading-tight font-bold text-transparent md:text-5xl lg:text-6xl xl:text-7xl">
            <span className="text-foreground">The</span> Process
          </div>
        </motion.div>

        {/* Process Cards */}
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {processPhases.map((phase, index) => (
            <motion.div
              key={phase.phaseNumber}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.2 }}
              className="h-80"
            >
              <CardFlip
                title={phase.title}
                subtitle={phase.subtitle}
                description={phase.description}
                features={phase.features}
                theme={phase.theme}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
